import { useEffect, useMemo, useState } from 'react';

import { useJiraTransitions } from '@/hooks/useJiraTransitions';
import type { JiraTransitionConfig, JiraTransitionStep } from '@/types';

import { TransitionFlowAccordion } from './TransitionFlowAccordion';

const DEFAULT_VALUE: JiraTransitionConfig = {
  openToClose: [],
  closeToOpen: [],
};

export interface TransitionsPanelProps {
  accountKey?: string;
  projectKey?: string;
  config?: Record<string, unknown>;
  value?: JiraTransitionConfig;
  onChange?: (value: JiraTransitionConfig) => void;
  disabled?: boolean;
}

export const TransitionsPanel = ({
  accountKey,
  projectKey,
  config,
  value,
  onChange,
  disabled = false,
}: TransitionsPanelProps) => {
  const [localValue, setLocalValue] =
    useState<JiraTransitionConfig>(DEFAULT_VALUE);
  const [suggestionsApplied, setSuggestionsApplied] = useState(false);

  const {
    data: transitionsData,
    isLoading,
    error,
  } = useJiraTransitions({
    accountKey,
    projectKey,
    config,
    enabled: !disabled && !!projectKey,
  });

  // Extract statuses from the response
  const transitions = transitionsData?.statuses ?? [];

  // Pure computation: effective value + optional suggestion to apply (single source of truth)
  const { effectiveValue, suggestionToApply } = useMemo((): {
    effectiveValue: JiraTransitionConfig;
    suggestionToApply: JiraTransitionConfig | null;
  } => {
    const baseValue = value ?? localValue;

    if (suggestionsApplied) {
      return {
        effectiveValue: baseValue,
        suggestionToApply: null,
      };
    }

    const hasNoOpenToClose = baseValue.openToClose.length === 0;
    const hasNoCloseToOpen = baseValue.closeToOpen.length === 0;
    const hasSuggestions =
      (transitionsData?.suggestedOpenToClose?.length ?? 0) > 0 ||
      (transitionsData?.suggestedCloseToOpen?.length ?? 0) > 0;

    if (
      !transitionsData ||
      !hasSuggestions ||
      (!hasNoOpenToClose && !hasNoCloseToOpen)
    ) {
      return {
        effectiveValue: baseValue,
        suggestionToApply: null,
      };
    }

    const toStep = (t: { id: string; name: string }): JiraTransitionStep => ({
      id: t.id,
      name: t.name,
    });
    const suggestedValue: JiraTransitionConfig = {
      openToClose: hasNoOpenToClose
        ? (transitionsData.suggestedOpenToClose?.map(toStep) ?? [])
        : baseValue.openToClose,
      closeToOpen: hasNoCloseToOpen
        ? (transitionsData.suggestedCloseToOpen?.map(toStep) ?? [])
        : baseValue.closeToOpen,
    };

    const shouldApply =
      suggestedValue.openToClose.length > 0 ||
      suggestedValue.closeToOpen.length > 0;
    return {
      effectiveValue: suggestedValue,
      suggestionToApply: shouldApply ? suggestedValue : null,
    };
  }, [value, localValue, transitionsData, suggestionsApplied]);

  useEffect(() => {
    if (!suggestionToApply) return;
    queueMicrotask(() => {
      setSuggestionsApplied(true);
      setLocalValue(suggestionToApply);
      onChange?.(suggestionToApply);
    });
  }, [suggestionToApply, onChange]);

  // Each flow should only filter out transitions already selected in THAT flow
  // The same transition can be used in both Open→Close and Close→Open flows
  const usedOpenToCloseIds = useMemo(
    () => new Set(effectiveValue.openToClose.map(s => s.id)),
    [effectiveValue.openToClose]
  );
  const usedCloseToOpenIds = useMemo(
    () => new Set(effectiveValue.closeToOpen.map(s => s.id)),
    [effectiveValue.closeToOpen]
  );
  const availableOpenToCloseTransitions = useMemo(
    () => transitions.filter(t => !usedOpenToCloseIds.has(t.id)),
    [transitions, usedOpenToCloseIds]
  );
  const availableCloseToOpenTransitions = useMemo(
    () => transitions.filter(t => !usedCloseToOpenIds.has(t.id)),
    [transitions, usedCloseToOpenIds]
  );

  const idsToSteps = (ids: string[]): JiraTransitionStep[] =>
    ids
      .map(id => {
        const t = transitions.find(tr => tr.id === id);
        return t ? { id: t.id, name: t.name } : null;
      })
      .filter((s): s is JiraTransitionStep => s !== null);

  const handleOpenToCloseChange = (transitionIds: string[]) => {
    const steps = idsToSteps(transitionIds);
    const newValue = { ...effectiveValue, openToClose: steps };
    setLocalValue(newValue);
    onChange?.(newValue);
  };

  const handleCloseToOpenChange = (transitionIds: string[]) => {
    const steps = idsToSteps(transitionIds);
    const newValue = { ...effectiveValue, closeToOpen: steps };
    setLocalValue(newValue);
    onChange?.(newValue);
  };

  if (disabled) {
    return (
      <div className="text-chariot-text-secondary flex h-full items-center justify-center text-sm">
        Select a project in the Configuration tab to enable transitions
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-chariot-text-secondary text-sm">
          Loading transitions...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-chariot-text-red text-sm">
          Failed to load transitions
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-chariot-text-primary text-lg font-semibold">
          Jira Transitions
        </h2>
        <p className="text-chariot-text-secondary text-sm">
          Configure which Jira transitions to use when opening or closing issues
        </p>
      </div>

      <TransitionFlowAccordion
        title="Open → Close Transitions"
        description="Transitions to use when closing an open Jira issue"
        transitions={transitions}
        selectedTransitionIds={effectiveValue.openToClose.map(s => s.id)}
        availableTransitions={availableOpenToCloseTransitions}
        onChange={handleOpenToCloseChange}
        disabled={disabled}
      />

      <TransitionFlowAccordion
        title="Close → Open Transitions"
        description="Transitions to use when reopening a closed Jira issue"
        transitions={transitions}
        selectedTransitionIds={effectiveValue.closeToOpen.map(s => s.id)}
        availableTransitions={availableCloseToOpenTransitions}
        onChange={handleCloseToOpenChange}
        disabled={disabled}
      />
    </div>
  );
};
